import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import Navbar from "../components/sub-comp/Navbar";
import Footer from "../components/sub-comp/MainFooter";
import FloatingSocialPanels from "../components/sub-comp/FloatingSocialPanels";
import cmsApi from "../services/cmsApi";
import { getImageUrl } from "../utils/getImageUrl";
import { getNewsById, getNewsList } from "../data/news";

const ShareButtons = ({ url, title }) => {
  const share = async () => {
    try {
      if (navigator.share) {
        await navigator.share({ title, url });
      } else if (navigator.clipboard) {
        await navigator.clipboard.writeText(url);
        alert("Link copied to clipboard");
      }
    } catch {}
  };
  return (
    <button onClick={share} className="px-4 py-2 rounded-full bg-custom-pink text-white hover:bg-purple-700 transition">
      Share
    </button>
  );
};

const NewsDetail = () => {
  const { id } = useParams();
  const [article, setArticle] = useState(null);
  const [allNews, setAllNews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch news article from CMS with fallback to hardcoded data
  useEffect(() => {
    if (typeof window === 'undefined' || !id) return;

    const fetchNewsData = async () => {
      try {
        setLoading(true);
        let foundArticle = null;
        let allNewsData = [];
        
        // Helper function to match article by ID
        const matchArticle = (article, searchId) => {
          const normalizedId = String(searchId).trim();
          const normalizedAId = String(article.id || '').trim();
          const normalizedSlug = String(article.slug || '').trim();
          
          // Try exact string match
          if (normalizedAId === normalizedId) return true;
          // Try numeric comparison
          const numId = Number(normalizedId);
          const numAId = Number(normalizedAId);
          if (!isNaN(numId) && !isNaN(numAId) && numId === numAId) return true;
          // Try slug match
          if (normalizedSlug === normalizedId) return true;
          return false;
        };
        
        // Step 1: Try to fetch from CMS News API first
        try {
          const result = await cmsApi.getNews({ limit: 100 });
          
          if (result.success && result.news && Array.isArray(result.news) && result.news.length > 0) {
            allNewsData = result.news;
            
            // Find the article by ID
            foundArticle = result.news.find(n => matchArticle(n, id));
            
            if (foundArticle) {
              console.log('Found article in CMS News API');
            }
          }
        } catch (apiError) {
          console.warn('CMS News API failed:', apiError);
        }
        
        // Step 2: If still not found, try hardcoded data as last resort
        if (!foundArticle) {
          console.log('Article not found in CMS, checking hardcoded data for ID:', id);
          foundArticle = getNewsById(id);
          if (foundArticle) {
            console.log('Found article in hardcoded data');
            if (allNewsData.length === 0) {
              allNewsData = getNewsList();
            }
          }
        }
        
        if (foundArticle) {
          setArticle(foundArticle);
          setAllNews(allNewsData);
        } else {
          console.error('Article not found in API or fallback data. ID:', id);
          setError('Article not found');
        }
      } catch (err) {
        console.error('Error fetching news:', err);
        // Last resort: try hardcoded data
        try {
          const fallbackArticle = getNewsById(id);
          const fallbackAll = getNewsList();
          if (fallbackArticle) {
            setArticle(fallbackArticle);
            setAllNews(fallbackAll);
          } else {
            setError('Article not found');
          }
        } catch (fallbackErr) {
          console.error('Fallback also failed:', fallbackErr);
          setError('Failed to load news');
        }
      } finally {
        setLoading(false);
      }
    };

    fetchNewsData();
  }, [id]);

  // Basic SEO: set page title and meta description
  useEffect(() => {
    if (!article) return;
    const prevTitle = document.title;
    document.title = `${article.title} | JRIIT News`;

    const metaDescription = document.querySelector('meta[name="description"]') || (() => {
      const m = document.createElement('meta');
      m.setAttribute('name', 'description');
      document.head.appendChild(m);
      return m;
    })();
    const prevDesc = metaDescription.getAttribute('content');
    metaDescription.setAttribute('content', article.excerpt || article.description || article.title);

    return () => {
      document.title = prevTitle;
      if (prevDesc !== null && prevDesc !== undefined) {
        metaDescription.setAttribute('content', prevDesc);
      }
    };
  }, [article]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Navbar />
        <div className="mid-desktop-page">
        
        {/* Loading Hero Section */}
        <section className="relative py-20 bg-gradient-to-r from-custom-pink to-purple-800 text-white">
          <div className="absolute inset-0 bg-black/20"></div>
          <div className="relative container mx-auto px-4">
            <div className="animate-pulse">
              <div className="h-4 bg-white/30 rounded w-1/3 mb-4"></div>
              <div className="h-12 bg-white/30 rounded w-3/4 mb-4"></div>
              <div className="flex gap-3">
                <div className="h-6 bg-white/30 rounded-full w-24"></div>
                <div className="h-6 bg-white/30 rounded w-32"></div>
                <div className="h-6 bg-white/30 rounded w-28"></div>
              </div>
            </div>
          </div>
        </section>

        {/* Loading Featured Image */}
        <section className="py-6 bg-white">
          <div className="container mx-auto px-4">
            <div className="animate-pulse">
              <div className="w-full h-[300px] md:h-[480px] bg-gray-200 rounded-xl"></div>
            </div>
          </div>
        </section>

        {/* Loading Article Body */}
        <section className="py-12">
          <div className="container mx-auto px-4 grid grid-cols-1 xl:grid-cols-4 gap-8">
            <article className="xl:col-span-3 bg-white rounded-xl shadow p-6">
              <div className="animate-pulse space-y-4">
                <div className="flex gap-2 mb-4">
                  <div className="h-6 bg-gray-200 rounded-full w-20"></div>
                  <div className="h-6 bg-gray-200 rounded-full w-16"></div>
                  <div className="h-6 bg-gray-200 rounded-full w-24"></div>
                </div>
                <div className="space-y-3">
                  <div className="h-4 bg-gray-200 rounded w-full"></div>
                  <div className="h-4 bg-gray-200 rounded w-full"></div>
                  <div className="h-4 bg-gray-200 rounded w-5/6"></div>
                  <div className="h-4 bg-gray-200 rounded w-full"></div>
                  <div className="h-4 bg-gray-200 rounded w-4/5"></div>
                  <div className="h-4 bg-gray-200 rounded w-full"></div>
                  <div className="h-4 bg-gray-200 rounded w-3/4"></div>
                </div>
                <div className="flex gap-3 mt-8">
                  <div className="h-10 bg-gray-200 rounded-full w-24"></div>
                  <div className="h-10 bg-gray-200 rounded-full w-20"></div>
                  <div className="h-10 bg-gray-200 rounded-full w-32 ml-auto"></div>
                </div>
              </div>
            </article>

            {/* Loading Sidebar */}
            <aside className="xl:col-span-1">
              <div className="bg-white rounded-xl shadow p-5">
                <div className="animate-pulse">
                  <div className="h-6 bg-gray-200 rounded w-32 mb-4"></div>
                  <div className="space-y-4">
                    <div className="flex gap-3">
                      <div className="w-16 h-16 bg-gray-200 rounded"></div>
                      <div className="flex-1">
                        <div className="h-4 bg-gray-200 rounded w-full mb-2"></div>
                        <div className="h-3 bg-gray-200 rounded w-2/3"></div>
                      </div>
                    </div>
                    <div className="flex gap-3">
                      <div className="w-16 h-16 bg-gray-200 rounded"></div>
                      <div className="flex-1">
                        <div className="h-4 bg-gray-200 rounded w-full mb-2"></div>
                        <div className="h-3 bg-gray-200 rounded w-2/3"></div>
                      </div>
                    </div>
                    <div className="flex gap-3">
                      <div className="w-16 h-16 bg-gray-200 rounded"></div>
                      <div className="flex-1">
                        <div className="h-4 bg-gray-200 rounded w-full mb-2"></div>
                        <div className="h-3 bg-gray-200 rounded w-2/3"></div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </aside>
          </div>
        </section>

        </div>

        <Footer />
      </div>
    );
  }

  if (error || !article) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Navbar />
        <div className="mid-desktop-page">
        <div className="container mx-auto px-4 py-20 text-center">
          <h1 className="text-3xl font-bold text-gray-800 mb-4">Article Not Found</h1>
          <p className="text-gray-600 mb-8">The article you're looking for doesn't exist or may have been moved.</p>
          <Link to="/news" className="inline-block px-6 py-3 rounded-full bg-custom-pink text-white hover:bg-purple-700 transition">Back to News</Link>
        </div>
        </div>
        <Footer />
      </div>
    );
  }

  const url = `${window.location.origin}/news/${article.id || article.slug || id}`;
  const related = allNews.filter((n) => 
    n.category === article.category && 
    (n.id !== article.id && n.slug !== article.slug)
  ).slice(0, 3);

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      <div className="mid-desktop-page">

      {/* Hero */}
      <section className="relative py-20 bg-gradient-to-r from-custom-pink to-purple-800 text-white">
        <div className="absolute inset-0 bg-black/20"></div>
        <div className="relative container mx-auto px-4">
          <nav className="text-white/80 text-sm mb-4">
            <Link to="/" className="hover:text-white">Home</Link>
            <span className="mx-2">/</span>
            <Link to="/news" className="hover:text-white">News</Link>
            <span className="mx-2">/</span>
            <span className="text-white">{article.title}</span>
          </nav>
          <h1 className="text-3xl md:text-5xl font-bold mb-4">{article.title}</h1>
          <div className="flex flex-wrap gap-3 items-center text-white/90">
            <span className="px-3 py-1 bg-gold text-black rounded-full font-bold text-sm">{article.category}</span>
            <span className="text-sm">{article.date}</span>
            <span className="text-sm">{article.author}</span>
            <span className="text-sm">{article.readTime}</span>
          </div>
        </div>
      </section>

      {/* Featured Image */}
      <section className="py-6 bg-white">
        <div className="container mx-auto px-4">
          <img 
            src={getImageUrl(article.featuredImage || article.image || article.thumbnail)} 
            alt={article.title} 
            className="w-full h-[300px] md:h-[480px] object-cover rounded-xl shadow" 
            onError={(e) => {
              console.error('Failed to load news image:', article.featuredImage || article.image);
              e.currentTarget.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="800" height="480"%3E%3Crect fill="%23ddd" width="800" height="480"/%3E%3Ctext fill="%23999" font-family="sans-serif" font-size="14" x="50%25" y="50%25" text-anchor="middle" dy=".3em"%3EImage not found%3C/text%3E%3C/svg%3E';
            }}
          />
        </div>
      </section>

      {/* Article Body */}
      <section className="py-12">
        <div className="container mx-auto px-4 grid grid-cols-1 xl:grid-cols-4 gap-8">
          <article className="xl:col-span-3 bg-white rounded-xl shadow p-6">
            <div className="flex flex-wrap gap-2 mb-4">
              {Array.isArray(article.tags) && article.tags.map((tag) => (
                <span key={tag} className="px-2 py-1 bg-custom-pink/10 text-custom-pink text-xs rounded-full">{tag}</span>
              ))}
            </div>
            <div className="prose max-w-none">
              <p className="text-gray-800 leading-relaxed whitespace-pre-line">{article.content || article.description || article.excerpt}</p>
            </div>
            <div className="mt-8 flex flex-wrap gap-3 items-center">
              <ShareButtons url={url} title={article.title} />
              <button onClick={() => window.print()} className="px-4 py-2 rounded-full bg-gray-200 text-gray-800 hover:bg-gray-300 transition">Print</button>
              <Link to="/news" className="ml-auto px-4 py-2 rounded-full bg-gold text-black hover:bg-yellow-400 transition">Back to News</Link>
            </div>
          </article>

          {/* Sidebar: Related */}
          <aside className="xl:col-span-1">
            <div className="bg-white rounded-xl shadow p-5">
              <h3 className="text-lg font-bold text-gray-900 mb-4">Related Articles</h3>
              <div className="space-y-4">
                {related.map((n) => (
                  <Link key={n.id || n.slug} to={`/news/${n.id || n.slug}`} className="flex gap-3 group">
                    <img 
                      src={getImageUrl(n.image || n.featuredImage || n.thumbnail)} 
                      alt={n.title} 
                      className="w-16 h-16 object-cover rounded"
                      onError={(e) => {
                        e.currentTarget.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="64" height="64"%3E%3Crect fill="%23ddd" width="64" height="64"/%3E%3C/svg%3E';
                      }}
                    />
                    <div>
                      <p className="text-sm font-semibold group-hover:text-custom-pink">{n.title}</p>
                      <p className="text-xs text-gray-500">{n.date || (n.publishedAt ? new Date(n.publishedAt).toLocaleDateString() : '')}</p>
                    </div>
                  </Link>
                ))}
                {related.length === 0 && (
                  <p className="text-gray-600 text-sm">No related articles.</p>
                )}
              </div>
            </div>
          </aside>
        </div>
      </section>

      </div>

      <Footer />
      <FloatingSocialPanels />
    </div>
  );
};

export default NewsDetail;


